import { type EnvironmentId, isManagedByAfar, type ServerConfig } from "@afar-research/contracts";

import type { UsagePagePreferences } from "./usagePagePreferences";

export type UsageMetric = UsagePagePreferences["metric"];

const METRIC_OPTIONS = [
  { value: "cost", label: "Cost" },
  { value: "tokens", label: "Tokens" },
  { value: "limits", label: "Limits" },
] as const satisfies readonly { value: UsageMetric; label: string }[];

export function isUsageMetric(value: string | null | undefined): value is UsageMetric {
  return METRIC_OPTIONS.some((option) => option.value === value);
}

/**
 * Whether to offer the Limits view: some selected Environment runs providers on their own
 * subscriptions. A Cloud Workspace's Codex draws on the account's Usage Allowance instead, which
 * the Account settings show. An Environment whose config has not arrived counts as it did before
 * Cloud, so a local Limits view does not drop to Cost while connecting, and with no Environment to
 * read the view stays offered.
 */
export function usageLimitsAvailable(
  selectedEnvironmentIds: ReadonlyArray<EnvironmentId>,
  serverConfigs: ReadonlyMap<EnvironmentId, Pick<ServerConfig, "providerManagementCapabilities">>,
): boolean {
  return (
    selectedEnvironmentIds.length === 0 ||
    selectedEnvironmentIds.some(
      (environmentId) => !isManagedByAfar(serverConfigs.get(environmentId)),
    )
  );
}

export function usageMetricOptions(limitsAvailable: boolean) {
  return limitsAvailable
    ? METRIC_OPTIONS
    : METRIC_OPTIONS.filter((option) => option.value !== "limits");
}

/** The metric to show for a stored preference. The preference itself is left as the user set it. */
export function resolveUsageMetric(stored: UsageMetric, limitsAvailable: boolean): UsageMetric {
  return stored === "limits" && !limitsAvailable ? "cost" : stored;
}
